/**
 * Route guard for the all-source lead inbox (ALL-SOURCE-LEAD-INBOX-V1,
 * B3 / Story 2.2). REUSES the org-intake access decision verbatim
 * (decideOrgIntakeAccess -- the ROUTABLE role superset): the inbox is
 * gated by the SAME config-backed owner-role mechanism server-side (the
 * org-default new_lead_followup routing_config row; default 'coc'; owner
 * supervises org-wide), so the frontend guard deliberately does NOT
 * hardcode a working role and adds NO second decision function. NO /me
 * flag, NO feature flag (internal surface, default-on per ADR-0016).
 *
 * Backend route auth on GET /api/lead-inbox remains the real security
 * boundary; a routable role that is not the configured owner gets the
 * endpoint's 401, which the page renders as the honest
 * access-restricted state.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideOrgIntakeAccess } from '@/dashboard/org-intake/org-intake-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireLeadInboxViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireLeadInboxView({
  loading,
  error,
  me,
  children,
}: RequireLeadInboxViewProps): JSX.Element | null {
  const decision = decideOrgIntakeAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireLeadInbox(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireLeadInboxView loading={ctx.loading} error={ctx.error} me={ctx.me} />
  );
}
